import { useCaseListingDrawerEvents, useWorkingDefinitionSelectedIncludedFields }
  from 'app/area/case-report/components/included-domains/case-listing-drawer-hooks';
import { dragPanelForItemType } from 'app/area/reports/components/drag-panel';
import { dropPanelForItemType } from 'app/area/reports/components/drop-panel';
import { FieldItem, ItemTypes } from 'app/area/reports/types';
import React, { useMemo } from 'react';
import { ExpandablePanel } from 'ui/expandable-panel/expandable-panel';
import styles from './case-listing-drawer.scss';
import { DropArea } from 'app/area/reports/components/drop-area';
import { SortItem } from './sort-item';
import { SelectedCaseField, moveField } from 'app/area/case-report/components/included-domains/case-listing-selected-item';
import { caseReportVisualisationTranslations } from 'app/area/case-report/constants';
import { DragNDrop } from 'app/area/reports/components/drag-n-drop';
import { ScrollOnDrag } from 'app/area/case-report/components/included-domains/ScrollOnDrag';
import { ButtonSelectMultipleDialog } from 'app/area/reports/components/button-select-multiple-dialog';
import uniqBy from 'lodash/uniqBy';
import { CaseIncludedField } from 'app/area/case-report/case-listing/types';

export type CaseReportVisualisationTranslations = { [key in keyof typeof caseReportVisualisationTranslations]: string };
export function CaseListingDrawer({ translation }: {
  translation: CaseReportVisualisationTranslations;
}) {
  const { defaultFields, setIncludedFields, additionalIncludedFields, fetchAdditionalIncludedFields,
    updateDefaultIncludedFields } = useCaseListingDrawerEvents();
  const selectedFields: FieldItem[] = useWorkingDefinitionSelectedIncludedFields();
  const IncludedFieldDragPanel = useMemo(() => dragPanelForItemType(ItemTypes.INCLUDED_DOMAINS_FIELD), [selectedFields]);
  const IncludedFieldDropPanel = useMemo(() => dropPanelForItemType(ItemTypes.INCLUDED_DOMAINS_FIELD), [selectedFields]);

  const onAddField = (item: FieldItem) => {
    onSelectedUpdate([...selectedFields, item]);
  };

  const onRemove = (item: FieldItem) => {
    const updatedList = selectedFields.filter(field => field.value !== item.value);
    onSelectedUpdate(updatedList);
  };

  const onAddAdditionalFieldUpdate = (items: FieldItem[]) => {
    const updated = uniqBy(items, 'value');
    updateDefaultIncludedFields(updated);
    onSelectedUpdate(updated);
  };

  const isFieldSelected = (field: FieldItem) => {
    return selectedFields?.some(item => item.value === field.value);
  };

  const findFieldIndex = (field: FieldItem) => {
    return selectedFields.indexOf(field);
  };

  const moveSelectedField = (droppedId: number, destinationId: number) => {
    const updated = moveField(selectedFields, droppedId, destinationId);
    onSelectedUpdate(updated);
  };

  const onSelectedUpdate = (items: FieldItem[]) => {
    const updatedList: CaseIncludedField[] = items.map((item, index) => {
      return {
        domain: {
          v: item.value,
          d: item.description,
          pad: item.parentAllLevelsDescription
        },
        entityTypePath: item.entityTypePath,
        id: index + 1
      };
    });
    setIncludedFields(updatedList);
  };

  const additionalIncludedFieldsDialog =
    <ButtonSelectMultipleDialog
      disabled={false}
      className={styles.dragTag}
      options={additionalIncludedFields || []}
      selectedItems={selectedFields}
      placeholder={translation.search_placeholder}
      textButtonAdd={`+ ${translation.add_new_fields}`}
      textTitle={translation.add_new_fields_title}
      textConfirm={translation.add_new_fields_done}
      onLoadOptions={fetchAdditionalIncludedFields}
      onSelect={onAddAdditionalFieldUpdate}
      displaySelectAll={true}
      textClearAll={translation.clear_all}
      textSelectAll={translation.select_all} />;

  ScrollOnDrag();
  return (<div className={styles.drawerWrapper}> <DragNDrop>
    <ExpandablePanel title={translation.title}>
      <div className="row">
        <div className="col-6">
          <ul className={styles.dragTagContainer}>
            {additionalIncludedFieldsDialog}
            {defaultFields && defaultFields.map(item => {
              return <IncludedFieldDragPanel
                key={item.value}
                className={styles.dragTag}
                field={item}
                isSelected={isFieldSelected(item)}
                onSelect={onAddField}
                added={item.added}
                onRemove={onRemove}
                subTitle={item.parentAllLevelsDescription}
              />;
            })}
            {additionalIncludedFieldsDialog}
          </ul>
        </div>
        <div className="col-6">
          <IncludedFieldDropPanel
            className={styles.dropBox}
            addItem={onAddField}>
            {
              selectedFields?.map(item =>
                <SortItem
                  key={item.value}
                  field={item}
                  findField={(field) => findFieldIndex(field)}
                  moveField={moveSelectedField}
                >
                  <SelectedCaseField
                    item={item}
                    onUpdate={onSelectedUpdate}
                    selectedItems={selectedFields}
                    translation={translation}
                  />
                </SortItem>
              )}
            <DropArea text={translation.drag_n_drop} />
          </IncludedFieldDropPanel>
        </div>
      </div>
    </ExpandablePanel>
  </DragNDrop>
  </div>
  );
}
